import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import Footer from './components/Footer';
import GoogleAnalytics from './components/GoogleAnalytics';
import InternalAnalyticsGuard from './components/InternalAnalyticsGuard';
import CookieConsent from './components/CookieConsent';
import CustomerTypeModal from './components/CustomerTypeModal';
import CheckoutRedirect from "./pages/CheckoutRedirect";
import ReviewPage from "./pages/ReviewPage";

// Eager load critical pages
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import { lazyWithRetry } from './utils/lazyWithRetry';

// Lazy load non-critical pages (med retry vid utdaterade chunks efter deploy)
const BookingPage = lazyWithRetry(() => import('./pages/BookingPage'));
const CheckoutPage = lazyWithRetry(() => import('./pages/CheckoutPage'));
const CheckoutFormPage = lazyWithRetry(() => import('./pages/CheckoutFormPage'));
const CheckoutSuccess = lazyWithRetry(() => import('./pages/CheckoutSuccess'));
const CheckoutCancel = lazyWithRetry(() => import('./pages/CheckoutCancel'));
const CheckoutSuccessPage = lazyWithRetry(() => import('./pages/CheckoutSuccessPage'));
const AboutPage = lazyWithRetry(() => import('./pages/AboutPage'));
const ContactPage = lazyWithRetry(() => import('./pages/ContactPage'));
const FaqPage = lazyWithRetry(() => import('./pages/FaqPage'));
const StudentPage = lazyWithRetry(() => import('./pages/StudentPage'));
const HyraLjudStudentfestPage = lazyWithRetry(() => import('./pages/HyraLjudStudentfestPage'));
const StudentflakLjudpaketPage = lazyWithRetry(() => import('./pages/StudentflakLjudpaketPage'));
const BusinessPage = lazyWithRetry(() => import('./pages/BusinessPage'));
const EventteknikForetagPage = lazyWithRetry(() => import('./pages/EventteknikForetagPage'));
const JulfestForetagPage = lazyWithRetry(() => import('./pages/JulfestForetagPage'));
const EventteknikFestivalPage = lazyWithRetry(() => import('./pages/EventteknikFestivalPage'));
const EventteknikMassorPage = lazyWithRetry(() => import('./pages/EventteknikMassorPage'));
const WeddingPage = lazyWithRetry(() => import('./pages/WeddingPage'));
const LjudLjusBrollopPage = lazyWithRetry(() => import('./pages/LjudLjusBrollopPage'));
const LjudLjusSkanePage = lazyWithRetry(() => import('./pages/LjudLjusSkanePage'));
const LjudLjusCityPage = lazyWithRetry(() => import('./pages/LjudLjusCityPage'));
const DJPage = lazyWithRetry(() => import('./pages/DJPage'));
const SaxofonistPage = lazyWithRetry(() => import('./pages/SaxofonistPage'));
const DjBookingRedirectPage = lazyWithRetry(() => import('./pages/DjBookingRedirectPage'));
const StagePage = lazyWithRetry(() => import('./pages/StagePage'));
const HyraScenMalmoPage = lazyWithRetry(() => import('./pages/HyraScenMalmoPage'));
const HyraHogtalareMalmoPage = lazyWithRetry(() => import('./pages/HyraHogtalareMalmoPage'));
const FestutrustningNasetPage = lazyWithRetry(() => import('./pages/FestutrustningNasetPage'));
const KomplettaFestpaketPage = lazyWithRetry(() => import('./pages/KomplettaFestpaketPage'));
const RokmaskinKonfettiEffekterPage = lazyWithRetry(() => import('./pages/RokmaskinKonfettiEffekterPage'));
const AdminPageWrapper = lazyWithRetry(() => import('./pages/AdminPageWrapper'));
const TermsPage = lazyWithRetry(() => import('./pages/TermsPage'));
const PrivacyPage = lazyWithRetry(() => import('./pages/PrivacyPage'));
const NewsletterUnsubscribePage = lazyWithRetry(() => import('./pages/NewsletterUnsubscribePage'));
const BlogPage = lazyWithRetry(() => import('./pages/BlogPage'));
const BlogPostPage = lazyWithRetry(() => import('./pages/BlogPostPage'));
const NotFoundPage = lazyWithRetry(() => import('./pages/NotFoundPage'));
const NFCScanPage = lazyWithRetry(() => import('./pages/NFCScanPage'));
const BulkCheckout = lazyWithRetry(() => import('./pages/bulk-checkout'));
const TagScanner = lazyWithRetry(() => import('./components/TagScanner'));
const QuoteViewPage = lazyWithRetry(() => import('./pages/QuoteViewPage'));
const OffertPage = lazyWithRetry(() => import('./pages/OffertPage'));
const OffertEventPage = lazyWithRetry(() => import('./pages/OffertEventPage'));
const CustomerPortalPage = lazyWithRetry(() => import('./pages/CustomerPortalPage'));
const PaymentRefreshPage = lazyWithRetry(() => import('./pages/PaymentRefreshPage'));
const DjAvailabilityResponsePage = lazyWithRetry(() => import('./pages/DjAvailabilityResponsePage'));
const SupplierPortal = lazyWithRetry(() => import('./pages/SupplierPortal'));
const SupplierRegistrationPage = lazyWithRetry(() => import('./pages/SupplierRegistrationPage'));
const SupplierConfirmEmailPage = lazyWithRetry(() => import('./pages/SupplierConfirmEmailPage'));
const AccountsPage = lazyWithRetry(() => import('./pages/AccountsPage'));
const PersonalPageWrapper = lazyWithRetry(() => import('./pages/PersonalPageWrapper'));
const DjPortal = lazyWithRetry(() => import('./pages/DjPortal'));
const DjRegistrationPage = lazyWithRetry(() => import('./pages/DjRegistrationPage'));
const TenantRegisterPage = lazyWithRetry(() => import('./pages/TenantRegisterPage'));
const BrollopsjaktenPage = lazyWithRetry(() => import('./pages/BrollopsjaktenPage'));
const BrollopsmassanPage = lazyWithRetry(() => import('./pages/BrollopsmassanPage'));
const BrollopsmassanHalsningPage = lazyWithRetry(() => import('./pages/BrollopsmassanHalsningPage'));
const TavlingsvillkorBrollopsjaktenPage = lazyWithRetry(() => import('./pages/TavlingsvillkorBrollopsjaktenPage'));
const PartnerInterestPage = lazyWithRetry(() => import('./pages/PartnerInterestPage'));
const QrRedirectPage = lazyWithRetry(() => import('./pages/QrRedirectPage'));
const BaraGifPage = lazyWithRetry(() => import('./pages/BaraGifPage'));

import { CartProvider } from './context/CartContext';
import { CustomerTypeProvider } from './context/CustomerTypeContext';
import WebsiteSchema from './components/WebsiteSchema';
import SocialMediaIntegration from './components/SocialMediaIntegration';
import ChatbotWidget from './components/ChatbotWidget';
import ContactChatWidget from './components/ContactChatWidget';
import PublicSiteGate from './components/PublicSiteGate';
import SupplierPortalGate from './components/SupplierPortalGate';
import PromoFromQueryHandler from './components/PromoFromQueryHandler';

// Loading component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Laddar...</p>
    </div>
  </div>
);

// Component to handle scroll to top on route change
const ScrollToTop: React.FC = () => {
  const location = useLocation();

  React.useEffect(() => {
    // Check if the location has a hash (like #faq)
    if (location.hash) {
      // If there's a hash, let the page handle the scrolling
      return;
    }
    
    // Otherwise, scroll to top
    window.scrollTo(0, 0);
  }, [location.pathname, location.search]);

  return null;
};

function AppLayout() {
  const location = useLocation();
  const isBrollopsjakten = location.pathname === "/brollopsjakten";
  const isAdmin = location.pathname.startsWith('/admin');
  const isPersonal = location.pathname.startsWith('/personal');
  const isDjPortal = location.pathname.startsWith('/dj-portal');
  const [isCustomerTypeModalOpen, setIsCustomerTypeModalOpen] = React.useState(false);
  return (
    <CartProvider>
      <CustomerTypeProvider>
        <div className="flex flex-col min-h-screen bg-slate-950">
          <ScrollToTop />
          <PromoFromQueryHandler />
          <InternalAnalyticsGuard />
          <GoogleAnalytics />
          <WebsiteSchema />
          <SocialMediaIntegration facebookPageId="61577953652127" />
          {!isAdmin && !isPersonal && !isDjPortal && (
            <Header onOpenCustomerTypeModal={() => setIsCustomerTypeModalOpen(true)} />
          )}
          <main
            className={`flex-grow relative ${
              isAdmin || isPersonal || isDjPortal ? '' : 'overflow-hidden'
            } ${
              isAdmin || isPersonal || isDjPortal ? "bg-[var(--admin-bg,#0f172a)]" : "bg-slate-950"
            } ${
              isAdmin || isPersonal || isDjPortal ? "pt-0" : (isBrollopsjakten ? "pt-0" : "pt-[var(--header-height,7rem)]")
            }`}
          >
                {!isAdmin && !isPersonal && !isDjPortal && (
                  <>
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-0 opacity-100 [background:radial-gradient(ellipse_at_top,rgba(124,58,237,0.25),transparent_55%),radial-gradient(ellipse_at_bottom,rgba(56,189,248,0.18),transparent_60%)]"
                    />
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-0 opacity-50 [background-image:linear-gradient(to_right,rgba(148,163,184,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.08)_1px,transparent_1px)] [background-size:48px_48px]"
                    />
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-slate-950 to-transparent"
                    />
                  </>
                )}
                <div className="relative z-10">
                  <PublicSiteGate>
                    <Suspense fallback={<LoadingSpinner />}>
                      <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/produkter" element={<ProductsPage />} />
                  <Route path="/produkter/:idOrSlug" element={<ProductDetailPage />} />
                  <Route path="/boka-dj/:djProfileId" element={<DjBookingRedirectPage />} />
                  <Route path="/booking/:id" element={<BookingPage />} />
                  <Route path="/kassa" element={<CheckoutPage />} />
                  <Route path="/kassa/formular" element={<CheckoutFormPage />} />
                  <Route path="/kassa/genomford" element={<CheckoutSuccess />} />
                  <Route path="/kassa/avbruten" element={<CheckoutCancel />} />
                  <Route path="/bokning-genomford" element={<CheckoutSuccessPage />} />
                  <Route path="/om-oss" element={<AboutPage />} />
                  <Route path="/kontakt" element={<ContactPage />} />
                  <Route path="/vanliga-fragor" element={<FaqPage />} />
                  <Route path="/student" element={<StudentPage />} />
                  <Route path="/hyra-ljud-studentfest" element={<HyraLjudStudentfestPage />} />
                  <Route path="/studentflak-ljudpaket" element={<StudentflakLjudpaketPage />} />
                  <Route path="/foretag" element={<BusinessPage />} />
                  <Route path="/julfest-foretag" element={<JulfestForetagPage />} />
                  <Route path="/eventteknik-foretag" element={<EventteknikForetagPage />} />
                  <Route path="/eventteknik-festival" element={<EventteknikFestivalPage />} />
                  <Route path="/eventteknik-massor" element={<EventteknikMassorPage />} />
                  <Route path="/brollop" element={<WeddingPage />} />
                  <Route path="/ljud-ljus-brollop" element={<LjudLjusBrollopPage />} />
                  <Route path="/ljud-ljus-skane" element={<LjudLjusSkanePage />} />
                  <Route path="/ljud-ljus-malmo" element={<LjudLjusCityPage citySlug="malmo" />} />
                  <Route path="/ljud-ljus-lund" element={<LjudLjusCityPage citySlug="lund" />} />
                  <Route path="/ljud-ljus-helsingborg" element={<LjudLjusCityPage citySlug="helsingborg" />} />
                  <Route path="/dj" element={<DJPage />} />
                  <Route path="/saxofonist" element={<SaxofonistPage />} />
                  <Route path="/scen" element={<StagePage />} />
                  <Route path="/hyra-scen-malmo" element={<HyraScenMalmoPage />} />
                  <Route path="/hyra-hogtalare-malmo" element={<HyraHogtalareMalmoPage />} />
                  <Route path="/festutrustning-naset" element={<FestutrustningNasetPage />} />
                  <Route path="/kompletta-festpaket" element={<KomplettaFestpaketPage />} />
                  <Route path="/rokmaskin-konfetti-effekter" element={<RokmaskinKonfettiEffekterPage />} />
                  <Route path="/brollopsjakten" element={<BrollopsjaktenPage />} />
                  <Route path="/brollopsmassan" element={<BrollopsmassanPage />} />
                  <Route path="/brollopsmassan/halsning" element={<BrollopsmassanHalsningPage />} />
                  <Route path="/tavlingsvillkor/brollopsjakten" element={<TavlingsvillkorBrollopsjaktenPage />} />
                  <Route path="/intresse-samarbete" element={<PartnerInterestPage />} />
                  <Route path="/go/:slug" element={<QrRedirectPage />} />
                  <Route path="/bara-gif" element={<BaraGifPage />} />
                  <Route path="/nyheter" element={<BlogPage />} />
                  <Route path="/recension" element={<ReviewPage />} />
                  <Route path="/nyheter/:slug" element={<BlogPostPage />} />
                  <Route path="/nfc/scan" element={<NFCScanPage />} />
                  <Route path="/nfc/scan/:id" element={<NFCScanPage />} />
                  <Route path="/bulk-checkout" element={<BulkCheckout />} />
                  <Route path="/tag/:id" element={<TagScanner />} />
                  <Route path="/offert" element={<OffertPage />} />
                  <Route path="/offert/event" element={<OffertEventPage />} />
                  <Route path="/offert/:id" element={<QuoteViewPage />} />
                  <Route path="/mina-bokningar" element={<CustomerPortalPage />} />
                  <Route path="/betala-igen" element={<PaymentRefreshPage />} />
                  <Route path="/dj/tillganglighet" element={<DjAvailabilityResponsePage />} />
                  <Route path="/registrera" element={<TenantRegisterPage />} />
                  <Route path="/admin" element={<AdminPageWrapper />} />
                  <Route path="/personal" element={<PersonalPageWrapper />} />
                  <Route path="/accounts" element={<AccountsPage />} />
                  <Route path="/leverantor" element={<SupplierPortalGate><SupplierPortal /></SupplierPortalGate>} />
                  <Route path="/leverantor/registrera" element={<SupplierPortalGate><SupplierRegistrationPage /></SupplierPortalGate>} />
                  <Route path="/leverantor/bekrafta" element={<SupplierConfirmEmailPage />} />
                  <Route path="/dj-portal" element={<DjPortal />} />
                  <Route path="/dj-portal/registrera" element={<DjRegistrationPage />} />
                  <Route path="/villkor" element={<TermsPage />} />
                  <Route path="/integritet" element={<PrivacyPage />} />
                  <Route path="/avregistrera" element={<NewsletterUnsubscribePage />} />
                  <Route path="*" element={<NotFoundPage />} />
                      </Routes>
                    </Suspense>
                  </PublicSiteGate>
                </div>
              </main>
              {!isAdmin && !isPersonal && !isDjPortal && (
                <>
                  <Footer onOpenCustomerTypeModal={() => setIsCustomerTypeModalOpen(true)} />
                  <CookieConsent />
                </>
              )}
              {!isAdmin && !isPersonal && !isDjPortal && (
                <CustomerTypeModal
                  forceOpen={isCustomerTypeModalOpen}
                  onClose={() => setIsCustomerTypeModalOpen(false)}
                />
              )}
              {/* <ChatbotWidget /> */}
              <ContactChatWidget />

            
            
              {/* Facebook SDK root div */}
              <div id="fb-root"></div>
            </div>
          </CustomerTypeProvider>
    </CartProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <AppLayout />
      </Router>
    </HelmetProvider>
  );
}

export default App;
